import { clearAccessToken, setAccessToken, subscribeToAuthSession } from '@/lib/auth-session'
import { clearAuthFromExtension, syncAuthToExtension } from '@/lib/extension-bridge'
import type { User } from '@/types'
import Clarity from '@microsoft/clarity'
import { create } from 'zustand'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isInitialized: boolean
  sessionError: string | null
  setAuth: (user: User, token: string) => void
  restoreAuth: (user: User, token: string) => void
  rotateToken: (token: string) => void
  updateUser: (user: User | null) => void
  clearAuth: (broadcast?: boolean) => void
  markInitialized: () => void
  setSessionError: (message: string | null) => void
}

const authClearedListeners = new Set<() => void>()

function notifyAuthCleared(): void {
  for (const listener of authClearedListeners) listener()
}

export function subscribeToAuthCleared(listener: () => void): () => void {
  authClearedListeners.add(listener)
  return () => authClearedListeners.delete(listener)
}

function identifyUser(user: User): void {
  if (import.meta.env.VITE_ENV === 'production') {
    Clarity.identify(
      String(user.id),
      undefined,
      undefined,
      user.full_name || user.first_name || user.email,
    )
  }
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  token: null,
  isAuthenticated: false,
  isInitialized: false,
  sessionError: null,
  setAuth: (user, token) => {
    setAccessToken(token)
    set({ user, token, isAuthenticated: true, isInitialized: true, sessionError: null })
    syncAuthToExtension(token)
    identifyUser(user)
  },
  restoreAuth: (user, token) => {
    setAccessToken(token)
    set({ user, token, isAuthenticated: true, isInitialized: true, sessionError: null })
    syncAuthToExtension(token)
    identifyUser(user)
  },
  rotateToken: (token) => {
    setAccessToken(token)
    set({ token })
    syncAuthToExtension(token)
  },
  updateUser: (user) => set({ user }),
  clearAuth: (broadcast = true) => {
    clearAccessToken(broadcast)
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isInitialized: true,
      sessionError: null,
    })
    clearAuthFromExtension()
    notifyAuthCleared()
  },
  markInitialized: () => set({ isInitialized: true }),
  setSessionError: (sessionError) => set({ sessionError }),
}))

subscribeToAuthSession((event) => {
  if (event.type === 'token') {
    setAccessToken(event.accessToken, false)
    useAuthStore.setState({ token: event.accessToken })
    syncAuthToExtension(event.accessToken)
    return
  }

  clearAccessToken(false)
  useAuthStore.setState({
    user: null,
    token: null,
    isAuthenticated: false,
    isInitialized: true,
    sessionError: null,
  })
  clearAuthFromExtension()
  notifyAuthCleared()
})
